/*
 * Shared Shrine components for the Dawn 16 rebuild.
 * Plain ES2019, no dependencies except window.Splide (assets/splide.min.js, loaded before this file).
 *
 *   <splide-component>  Splide slider, options read from data-* attributes (Shrine PRO API)
 *   <internal-video>    lazy video: autoplay (muted, in viewport) only when data-autoplay="true",
 *                       otherwise nothing is downloaded until the visitor taps play
 *   <dynamic-dates>     fills [start_date] / [end_date] in delivery estimates
 *   <copy-button>       no-op stub (Shrine editor helper), renders nothing
 */
(function () {
  'use strict';

  var isRtl = document.documentElement.getAttribute('dir') === 'rtl';
  var defaultDirection = isRtl ? 'rtl' : 'ltr';

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  function intOr(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  /* ------------------------------------------------------------------ */
  /* splide-component                                                    */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('splide-component')) {
    class SplideComponent extends HTMLElement {
      constructor() {
        super();
        this.splide = null;
        this.onResize = null;
        this.onSectionLoad = this.onSectionLoad.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
      }

      connectedCallback() {
        document.addEventListener('shopify:section:load', this.onSectionLoad);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        this.initSlider();
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:section:load', this.onSectionLoad);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.destroySlider();
      }

      onSectionLoad(event) {
        if (event.target && event.target.contains && event.target.contains(this)) {
          this.destroySlider();
          this.initSlider();
        }
      }

      onBlockSelect(event) {
        if (!this.splide || !event.target || !this.contains(event.target)) return;
        var slide = event.target.closest('.splide__slide');
        if (!slide) return;
        var slides = this.splide.Components.Elements.slides;
        var index = slides.indexOf(slide);
        if (index > -1) this.splide.go(index);
      }

      buildConfig() {
        var d = this.dataset;
        var type = d.type || 'slide';
        var typeMobile = d.typeMobile || type;
        var drag = d.drag === 'false' ? false : d.drag === 'free' ? 'free' : true;
        var arrowsColor = d.arrowsColor ? ' color-' + d.arrowsColor : '';
        var dotsColor = d.dotsColor ? ' color-' + d.dotsColor + ' dots-custom-color' : '';
        var paddingCalcDesktop = d.paddingCalcDesktop === 'true';
        var rightPaddingDesktop = intOr(d.sidePaddingDesktop, 0);
        /* Shrine quirk preserved: any data-padding-calc-desktop value zeroes the left padding */
        var leftPaddingDesktop = d.paddingCalcDesktop ? 0 : rightPaddingDesktop;
        var paddingCalcMobile = d.paddingCalcMobile === 'true';
        var rightPaddingMobile = intOr(d.sidePaddingMobile, 0);
        var leftPaddingMobile = paddingCalcMobile ? 0 : rightPaddingMobile;
        var omitEnd = d.omitEnd !== 'false';

        return {
          type: type,
          direction: d.direction || defaultDirection,
          rewind: type === 'fade',
          autoplay: d.autoplay === 'true',
          interval: d.autoplaySpeed && !isNaN(parseInt(d.autoplaySpeed, 10)) ? 1000 * parseInt(d.autoplaySpeed, 10) : 5000,
          drag: drag,
          focus: d.focus || 0,
          trimSpace: d.trimSpace !== 'false',
          arrows: d.arrows !== 'false',
          pagination: d.pagination !== 'false',
          omitEnd: omitEnd,
          perPage: intOr(d.slidesDesktop, 3),
          perMove: intOr(d.perMoveDesktop, 1),
          autoWidth: d.autoWidth === 'true',
          gap: d.gapDesktop ? intOr(d.gapDesktop, 30) : 30,
          paddingCalc: paddingCalcDesktop,
          padding: { left: leftPaddingDesktop, right: rightPaddingDesktop },
          destroy: d.destroyDesktop === 'true',
          classes: {
            arrow: 'splide__arrow' + arrowsColor,
            page: 'splide__pagination__page' + dotsColor,
          },
          easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
          breakpoints: {
            749: {
              omitEnd: omitEnd,
              type: typeMobile,
              perPage: intOr(d.slidesMobile, 1),
              perMove: intOr(d.perMoveMobile, 1),
              gap: d.gapMobile ? intOr(d.gapMobile, 15) : 15,
              paddingCalc: paddingCalcMobile,
              padding: { left: leftPaddingMobile, right: rightPaddingMobile },
              destroy: d.destroyMobile === 'true',
            },
          },
        };
      }

      initSlider() {
        if (this.splide) return;
        var container = this.querySelector('.splide');
        if (!container) return;
        if (typeof window.Splide !== 'function') {
          /* splide.min.js is deferred before this file; retry once the page has loaded */
          var self = this;
          window.addEventListener(
            'load',
            function () {
              if (typeof window.Splide === 'function') self.initSlider();
            },
            { once: true }
          );
          return;
        }

        var splide = new window.Splide(container, this.buildConfig());
        var self = this;
        this.splide = splide;

        splide.on('mounted', function () {
          var slide = splide.Components.Elements.slides[splide.index];
          if (slide) {
            var img = slide.querySelector('img');
            var activate = function () {
              slide.classList.add('is-instant-active');
              self.setActiveSlideHeight(slide);
            };
            if (img && !img.complete) {
              img.addEventListener('load', activate, { once: true });
            } else {
              activate();
            }
          }
          self.onResize = debounce(function () {
            if (!self.splide) return;
            self.setActiveSlideHeight(self.splide.Components.Elements.slides[self.splide.index]);
          }, 200);
          window.addEventListener('resize', self.onResize);
        });

        splide.on('move', function () {
          var slides = splide.Components.Elements.slides;
          if (!slides) return;
          var current = slides[splide.index];
          if (!current) return;
          slides.forEach(function (s) {
            s.classList.remove('is-instant-active');
          });
          current.classList.add('is-instant-active');
          self.setActiveSlideHeight(current);
        });

        if (this.dataset.pauseVideos === 'true') {
          splide.on('hidden', function (slideComponent) {
            var video = slideComponent.slide.querySelector('internal-video');
            if (!video || !video.video) return;
            var action = video.dataset.actionOnInactive;
            if (action === 'pause') {
              video.video.pause();
              video.classList.remove('internal-video--playing');
            } else if (action === 'mute') {
              video.video.muted = true;
              video.classList.add('internal-video--muted');
            }
          });
        }

        splide.mount();
      }

      destroySlider() {
        if (this.onResize) {
          window.removeEventListener('resize', this.onResize);
          this.onResize = null;
        }
        if (this.splide) {
          try {
            this.splide.destroy(true);
          } catch (e) {
            /* already destroyed */
          }
          this.splide = null;
        }
      }

      setActiveSlideHeight(slide) {
        if (slide) this.style.setProperty('--active-slide-height', slide.offsetHeight + 'px');
      }
    }
    customElements.define('splide-component', SplideComponent);
  }

  /* ------------------------------------------------------------------ */
  /* internal-video                                                      */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('internal-video')) {
    var activeSoundVideo = null;

    class InternalVideo extends HTMLElement {
      constructor() {
        super();
        this.video = null;
        this.observer = null;
        this.inView = false;
        this.userPaused = false;
        this.loaded = false;
      }

      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video || this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';

        this.autoplay = this.dataset.autoplay === 'true';
        this.noPlayButton = this.dataset.noPlayBtn === 'true';
        this.playButton = this.querySelector('.internal-video__play');
        this.soundButton = this.querySelector('.internal-video__sound-btn');
        this.timeline = this.querySelector('.internal-video__timeline');

        /* Never let the browser fetch anything before we decide to */
        this.video.removeAttribute('autoplay');
        if (!this.autoplay) {
          this.video.setAttribute('preload', 'none');
          this.classList.remove('internal-video--playing');
        } else {
          this.video.muted = true;
          this.video.setAttribute('muted', '');
          this.classList.add('internal-video--muted');
        }

        this.bindEvents();
        this.observe();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        this.observer = null;
        if (this.video) this.video.pause();
        if (activeSoundVideo === this) activeSoundVideo = null;
        delete this.dataset.ready;
      }

      bindEvents() {
        var self = this;
        var video = this.video;

        var toggle = function (event) {
          if (event) {
            event.preventDefault();
            event.stopPropagation();
          }
          self.onTap();
        };
        if (this.playButton) this.playButton.addEventListener('click', toggle);
        video.addEventListener('click', toggle);

        if (this.soundButton) {
          this.soundButton.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            self.setMuted(!video.muted);
            if (video.paused) self.play();
          });
        }

        video.addEventListener('playing', function () {
          self.classList.add('internal-video--playing');
          self.classList.remove('internal-video--loading');
        });
        video.addEventListener('pause', function () {
          if (!self.autoplay) self.classList.remove('internal-video--playing');
        });
        video.addEventListener('waiting', function () {
          self.classList.add('internal-video--loading');
        });
        video.addEventListener('canplay', function () {
          self.classList.remove('internal-video--loading');
        });
        video.addEventListener('volumechange', function () {
          self.classList.toggle('internal-video--muted', video.muted);
        });

        if (this.timeline) {
          video.addEventListener('timeupdate', function () {
            if (!video.duration) return;
            self.timeline.style.setProperty('--completed', (video.currentTime / video.duration) * 100 + '%');
          });
          var seek = function (event) {
            if (!video.duration) return;
            var rect = self.timeline.getBoundingClientRect();
            var point = event.touches ? event.touches[0].clientX : event.clientX;
            var ratio = Math.min(Math.max((point - rect.left) / rect.width, 0), 1);
            video.currentTime = ratio * video.duration;
          };
          var dragging = false;
          this.timeline.addEventListener('pointerdown', function (event) {
            event.stopPropagation();
            dragging = true;
            seek(event);
          });
          window.addEventListener('pointermove', function (event) {
            if (dragging) seek(event);
          });
          window.addEventListener('pointerup', function () {
            dragging = false;
          });
          this.timeline.addEventListener('click', function (event) {
            event.stopPropagation();
          });
        }
      }

      observe() {
        var self = this;
        if (!('IntersectionObserver' in window)) {
          if (this.autoplay) this.play();
          return;
        }
        this.observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              self.inView = entry.isIntersecting;
              if (entry.isIntersecting) {
                if (self.autoplay && !self.userPaused) self.play();
              } else if (!self.video.paused) {
                self.video.pause();
                /* autoplay videos resume when they come back; tapped videos stay paused */
                if (!self.autoplay) self.classList.remove('internal-video--playing');
              }
            });
          },
          { rootMargin: '0px 0px 100px 0px', threshold: 0.1 }
        );
        this.observer.observe(this);
      }

      loadSources() {
        if (this.loaded) return;
        this.loaded = true;
        var changed = false;
        this.video.querySelectorAll('source[data-src]').forEach(function (source) {
          source.setAttribute('src', source.getAttribute('data-src'));
          source.removeAttribute('data-src');
          changed = true;
        });
        if (this.video.dataset.src) {
          this.video.setAttribute('src', this.video.dataset.src);
          this.video.removeAttribute('data-src');
          changed = true;
        }
        this.video.setAttribute('preload', 'auto');
        if (changed) this.video.load();
      }

      play() {
        this.loadSources();
        var promise = this.video.play();
        var self = this;
        if (promise && typeof promise.catch === 'function') {
          promise.catch(function () {
            /* autoplay blocked (low-power mode etc.): show the play button again */
            self.classList.remove('internal-video--playing');
          });
        }
      }

      setMuted(muted) {
        this.video.muted = muted;
        this.classList.toggle('internal-video--muted', muted);
        if (!muted) this.claimSound();
      }

      /* only one video plays with sound at a time */
      claimSound() {
        if (activeSoundVideo && activeSoundVideo !== this && activeSoundVideo.video) {
          var other = activeSoundVideo;
          if (other.autoplay) {
            other.setMuted(true);
          } else {
            other.video.pause();
          }
        }
        activeSoundVideo = this;
      }

      onTap() {
        var video = this.video;
        if (this.autoplay && video.muted && !this.noPlayButton) {
          /* muted preview tapped: restart with sound */
          this.userPaused = false;
          video.currentTime = 0;
          this.setMuted(false);
          this.play();
          return;
        }
        if (video.paused) {
          this.userPaused = false;
          if (!this.autoplay) this.claimSound();
          this.play();
        } else {
          this.userPaused = true;
          video.pause();
          this.classList.remove('internal-video--playing');
        }
      }
    }
    customElements.define('internal-video', InternalVideo);
  }

  /* ------------------------------------------------------------------ */
  /* dynamic-dates                                                       */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('dynamic-dates')) {
    var DEFAULT_DAYS = 'Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday';
    var DEFAULT_MONTHS = 'January, February, March, April, May, June, July, August, September, October, November, December';

    var splitLabels = function (value, fallback, count) {
      var list = (value || fallback).split(',').map(function (s) {
        return s.trim();
      });
      return list.length === count ? list : fallback.split(',').map(function (s) {
        return s.trim();
      });
    };

    var ordinal = function (n) {
      var v = n % 100;
      if (v >= 11 && v <= 13) return n + 'th';
      switch (n % 10) {
        case 1:
          return n + 'st';
        case 2:
          return n + 'nd';
        case 3:
          return n + 'rd';
        default:
          return n + 'th';
      }
    };

    var pad = function (n) {
      return n < 10 ? '0' + n : String(n);
    };

    class DynamicDates extends HTMLElement {
      connectedCallback() {
        this.days = splitLabels(this.dataset.dayLabels, DEFAULT_DAYS, 7); /* Monday first */
        this.months = splitLabels(this.dataset.monthLabels, DEFAULT_MONTHS, 12);
        this.format = this.dataset.dateFormat || 'day_mm_dd';
        this.render();
      }

      formatDate(date) {
        var dayName = this.days[(date.getDay() + 6) % 7];
        var monthName = this.months[date.getMonth()];
        var d = date.getDate();
        var m = date.getMonth() + 1;
        switch (this.format) {
          case 'day_dd_mm':
            return dayName + ', ' + d + '. ' + monthName;
          case 'mm_dd':
            return monthName + ' ' + ordinal(d);
          case 'dd_mm':
            return d + '. ' + monthName;
          case 'dd_mm_no_dot':
            return d + ' ' + monthName;
          case 'day_dd_mm_numeric':
            return dayName + ', ' + pad(d) + '. ' + pad(m) + '.';
          case 'dd_mm_numeric':
            return pad(d) + '. ' + pad(m) + '.';
          case 'day_mm_dd':
          default:
            return dayName + ', ' + monthName + ' ' + ordinal(d);
        }
      }

      addDays(days) {
        var date = new Date();
        date.setHours(12, 0, 0, 0);
        date.setDate(date.getDate() + days);
        return date;
      }

      render() {
        var self = this;
        this.querySelectorAll('[data-dynamic-date]').forEach(function (el) {
          var text = el.dataset.text;
          if (!text) return;
          var start = self.formatDate(self.addDays(intOr(el.dataset.minDays, 0)));
          var end = self.formatDate(self.addDays(intOr(el.dataset.maxDays, 0)));
          el.innerHTML = text.split('[start_date]').join(start).split('[end_date]').join(end);
        });
      }
    }
    customElements.define('dynamic-dates', DynamicDates);
  }

  /* ------------------------------------------------------------------ */
  /* copy-button (no-op)                                                 */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('copy-button')) {
    class CopyButton extends HTMLElement {
      connectedCallback() {
        this.hidden = true;
        this.style.display = 'none';
      }
    }
    customElements.define('copy-button', CopyButton);
  }
})();
